import React from "react";
import Image from "../../Image";
import { Circle, Group, Rect } from "react-konva";
import { toast } from "react-toastify";
import ObjectMetrics from "./sinkMetric";
import { useDispatch, useSelector } from "react-redux";
import { getSinks, updateSinkPos } from "../../../lib/redux/slices/sink-slice";
import { getHoles, updateHolePos } from "../../../lib/redux/slices/holes-slice";
import {
  addMeasurement,
  getMeasurementCoordinates,
} from "../../../lib/redux/slices/measurements-slice";
import { getMeasurement } from "./measurement-utils";
import { getElementRotations } from "../../../utils/canvas/getRotations";
import config from "../../../utils/config";
import { getDragBound } from "../../../utils/canvas/getDragBound";
import {
  getCurrentEvent,
  getDimensions,
  getLineAlignment,
  getTileShape,
} from "../../../lib/redux/slices/dimensions-slice";
import { getCanvasValues, wrtRatio } from "../../../utils/canvas";
import { useDebounce } from "use-debounce";
import { moveWithInLineEdges } from "./moveWithInLineEdges-utils";

const haveIntersection = (r1, r2) => {
  return !(
    r2.x > r1.x + r1.width ||
    r2.x + r2.width < r1.x ||
    r2.y > r1.y + r1.height ||
    r2.y + r2.height < r1.y
  );
};

const notify = (id) => {
  toast(
    `It's not possible to have two Object in the same location. Please remove one of them.`,
    {
      toastId: id,
      type: "warning",
      pauseOnHover: false,
      pauseOnFocusLoss: false,
      autoClose: 1500,
    }
  );
};

const SinkGroups = ({
  canvasOffsetX,
  canvasOffsetY,
  setCanvasOffsetX,
  setCanvasOffsetY,
  setCanvasOffsetX1,
  setCanvasOffsetY1,
}) => {
  const sinkCoordinate = useSelector(getMeasurementCoordinates);
  const sinks = useSelector(getSinks);
  const holes = useSelector(getHoles);

  // Group data by rotation
  const groupedData = {};
  for (const key in sinkCoordinate) {
    const element = sinkCoordinate[key];

    if (!("rotation" in element)) {
      continue;
    }

    if (!groupedData[element.rotation]) {
      groupedData[element.rotation] = [];
    }
    groupedData[element.rotation].push(element);
  }

  return (
    <>
      <Group>
        {sinks?.map((sink, index) => {
          return (
            <SinkGroup
              {...{
                key: `sinks-${index}`,
                index: `sinks-${sink.uniqId}`,
                sink,
                type: "sink",
                originalIndex: index,
                canvasOffsetX,
                canvasOffsetY,
              }}
            />
          );
        })}
        {holes?.map((sink, index) => {
          return (
            <SinkGroup
              {...{
                key: `holes-${index}`,
                index: `holes-${sink.uniqId}`,
                sink,
                type: "hole",
                originalIndex: index,
                canvasOffsetX,
                canvasOffsetY,
              }}
            />
          );
        })}
      </Group>

      {/* Measurement Metrics */}
      {[0, 90, "-90", 180, 270].map((metricRotation) => {
        return (
          <ObjectMetrics
            {...{
              groupedData,
              metricRotation,
              setCanvasOffsetX,
              setCanvasOffsetY,
              setCanvasOffsetX1,
              setCanvasOffsetY1,
            }}
          />
        );
      })}
    </>
  );
};

const SinkGroup = ({
  sink,
  index,
  type,
  originalIndex,
  canvasOffsetX,
  canvasOffsetY,
}) => {
  const dispatch = useDispatch();
  const groupRef = React.useRef();
  const currentShape = useSelector(getTileShape);
  const currentEvent = useSelector(getCurrentEvent);
  const lineAlignment = useSelector(getLineAlignment);
  const { top, left, bottom, right } = useSelector(getDimensions);
  const tileGap =
    type === "sink"
      ? sink.sinkGap
      : type === "hole"
      ? sink.holeGap
      : config.tileGap;
  const {
    canvasTop: cTop,
    canvasLeft: cLeft,
    canvasRight: cRight,
    canvasBottom: cBottom,
    canvasTop,
    canvasLeft,
    canvasRight,
    canvasBottom,
  } = getCanvasValues({ top, left, bottom, right }, currentShape, lineAlignment);


  // const [canvasTop] = useDebounce(cTop, config.debounceTime);
  // const [canvasLeft] = useDebounce(cLeft, config.debounceTime);
  // const [canvasRight] = useDebounce(cRight, config.debounceTime);
  // const [canvasBottom] = useDebounce(cBottom, config.debounceTime);

  // const [dimensions, setDimensions] = React.useState(null);
  const [collidingElementId, setCollidingElementId] = React.useState(null);
  const [originalPositions, setOriginalPositions] = React.useState({});

  const imageRatio = config.imageRatio;
  const defaultRatio = config.defaultRatio;
  const sinkImage =
    type === "hole"
      ? sink.image
      : (sink.mounting && (sink.mounting.label === config.defaultMounting || sink.mounting.label === 'isvetsning'))
      ? sink.inner
      : sink.image;

  let maxX = config.maxX;
  let maxY = config.maxY;

  const imageWidth = sink.width;
  const imageHeight = sink.height;

  let groupWidth = imageWidth / ( type === "hole" && sink.type !== 'circle' ? defaultRatio + (type === "hole" && sink.type === 'square' ? 0.6 : 0)  : imageRatio);
  let groupHeight = imageHeight / ( type === "hole" && sink.type !== 'circle' ? defaultRatio + (type === "hole" && sink.type === 'square' ? 0.6 : 0) : imageRatio);

  const pump_vanster =
    type === "sink"
      ? "variant" in sink &&
        sink.variant["pump_vanster"] &&
        sink.variant.pump_vanster.value
      : false;

  const pump_hoger =
    type === "sink"
      ? "variant" in sink &&
        sink.variant["pump_hoger"] &&
        sink.variant.pump_hoger.value
      : false;

  const blandare =
    type === "sink"
      ? "variant" in sink &&
        sink.variant["blandare"] &&
        sink.variant.blandare.value
      : false;

  const element = groupRef?.current;
  const elementId = `element-${index}`;

  let isLeft = currentShape === "left";
  let isRight = currentShape === "right";
  let isSimple = currentShape === "simple";

  React.useEffect(() => {
    if (element && sink) {
      moveWithInLineEdges({
        element,
        canvasTop,
        canvasBottom,
        canvasLeft,
        canvasRight,
        tileGap,
        currentShape,
        handleDragEnd,
        sink,
        currentEvent,
      });
    }
  }, [element, canvasTop, canvasBottom, canvasLeft, canvasRight, tileGap]);

  const handleDragStart = (e) => {
    const element = e.target;
    const elementId = element.getId();
    const { x, y } = element.position();

    setOriginalPositions((prevPositions) => ({
      ...prevPositions,
      [elementId]: {
        x,
        y,
        rotation: element.rotation(),
        offsetX: element.offsetX(),
        offsetY: element.offsetY(),
      },
    }));
  };

  const handleDragMove = (e) => {
    const element = e.target;
    const layer = element.parent;
    const tileGap =
      type === "sink"
        ? sink.sinkGap
        : type === "hole"
        ? sink.holeGap
        : config.tileGap;

    const targetRect = element.getClientRect();
    element.moveToTop();
    getElementRotations(
      element,
      currentShape,
      canvasTop,
      canvasLeft,
      canvasRight,
      canvasBottom,
      tileGap
    );

    layer.children.forEach(function (group) {
      const groupObject = group.findOne("Image");

      const elementId = group.getId();
      if (element === group) {
        return;
      }


      groupObject.stroke("");
      groupObject.strokeWidth(0);
      groupObject.cornerRadius(0);
      groupObject.opacity(1);
      //Not needed for now
      // if (haveIntersection(group.getClientRect(), targetRect)) {
      //   setCollidingElementId(elementId);
      //   groupObject.stroke("#b52f40");
      //   groupObject.strokeWidth(3);
      //   groupObject.cornerRadius(6);
      //   groupObject.opacity(0.9);
      // } else if (elementId !== collidingElementId) {
      //   groupObject.stroke("");
      //   groupObject.strokeWidth(0);
      //   groupObject.cornerRadius(0);
      //   groupObject.opacity(1);
      // } else {
      //   setCollidingElementId(null);
      // }
    });

    
  };

  const handleDragEnd = (e, collision = true) => {
    const tileGap =
      type === "sink"
        ? sink.sinkGap
        : type === "hole"
        ? sink.holeGap
        : config.tileGap;
    const element = e;


    // element.position({
    //   x: element.x(),
    //   y:element.y() > tileGap && element.rotation() === 180 ? tileGap : canvasRight,
    //   offsetX: element.offsetX(),
    //   offsetY: element.offsetY(),
    // });

    // if (collidingElementId && collision) {
    //   layer.children.forEach((group) => {
    //     const groupObject = group.findOne("Image");
    //     groupObject.stroke("");
    //     groupObject.strokeWidth(0);
    //     groupObject.cornerRadius(0);
    //     groupObject.opacity(1);
    //     notify(elementId);
    //   });

    //   const originalPosition = originalPositions[elementId];
    //   if (originalPosition) {
       
    //     element.rotation(originalPosition.rotation);
    //   }
    // } else {
      
    // }

    /** Update the current pos in sink / holes */

    let _x = element.x();
    let _y = element.y();

    if (type === "sink")
      dispatch(
        updateSinkPos({
          index: originalIndex,
          x: _x * defaultRatio,
          y: _y * defaultRatio,
          offsetX: element.offsetX(),
          offsetY: element.offsetY(),
          rotation: element.rotation(),
        })
      );
    else if (type === "hole")
      dispatch(
        updateHolePos({
          index: originalIndex,
          x: _x * defaultRatio,
          y: _y * defaultRatio,
          offsetX: element.offsetX(),
          offsetY: element.offsetY(),
          rotation: element.rotation(),
        })
      );

    dispatch(
      addMeasurement(
        getMeasurement({
          element,
          groupWidth,
          groupHeight,
          pump_hoger,
          pump_vanster,
          currentShape,
          canvasLeft,
          canvasRight,
          canvasTop,
          tileGap,
        })
      )
    );
    let __y = element.y();

    /*
    ** on drag placement within edge
    */
    if(isSimple && sink.type !== 'circle') {
      if( element.rotation() === 180 ) {
        __y = element.offsetY() + tileGap / defaultRatio
      }else if(element.rotation() === 0) {
        __y = canvasLeft - element.offsetY() - tileGap / defaultRatio
      }
    }
    
    if(isLeft && sink.type !== 'circle') {
      const isinTop = element.y() + element.height() / 2 > canvasRight
      if(isinTop) {
        if(element.rotation() === 0) {
          __y = canvasLeft - element.offsetY() - tileGap / defaultRatio
        }
      }else {
        if( element.rotation() === 180 ) {
          __y = element.offsetY() + tileGap / defaultRatio
        }else if(element.rotation() === 0) {
          __y = canvasRight - element.offsetY() - tileGap / defaultRatio
        }
      }
    }

    if(isRight && sink.type !== 'circle') {
      const isinTop = element.y() + element.height() / 2 > canvasLeft
      if(isinTop) {
        if(element.rotation() === 0) {
          __y = canvasRight - element.offsetY() - tileGap / defaultRatio
        }
      }else {
        if( element.rotation() === 180 ) {
          __y = element.offsetY() + tileGap / defaultRatio
        }else if(element.rotation() === 0) {
          __y = canvasLeft - element.offsetY() - tileGap / defaultRatio
        }
      }
    }

    element.position({
      x: element.x(),
      y: __y ,
      offsetX: element.offsetX(),
      offsetY: element.offsetY(),
    });

  };
  let _x = wrtRatio(sink.x, "/");
  let _y = wrtRatio(sink.y, "/");

  const holeImage =
    process.env.NODE_ENV === "production"
      ? "/builderonly/hole.png"
      : "/hole.png";

  return (
    <Group
      ref={groupRef}
      key={elementId}
      id={elementId}
      x={sink.x ? _x : (canvasTop - groupWidth) / 2 + groupWidth / 2}
      y={sink.y ? _y : (canvasLeft + groupHeight) / 2 + groupHeight}
      offsetX={groupWidth / 2}
      offsetY={groupHeight / 2}
      width={groupWidth}
      height={groupHeight}
      draggable
      dragBoundFunc={function (pos) {
        return getDragBound({
          pos,
          element: this,
          currentShape,
          canvasTop,
          canvasLeft,
          canvasRight,
          canvasBottom,
          canvasOffsetX,
          canvasOffsetY,
          maxX,
          maxY,
          tileGap: tileGap,
        });
      }}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={(e) => handleDragEnd(e.target)}
    >
      <Image
        width={groupWidth}
        height={groupHeight}
        title="image"
        url={sinkImage}
      />

      <Group id={elementId}>
        {pump_vanster ? (
          <Group offsetX={-3} offsetY={16}>
            <Image width={10} height={10} title="image" url={holeImage} />
          </Group>
        ) : // <Circle
        //   width={6}
        //   height={6}
        //   fill="#fff"
        //   x={10}
        //   y={-10}
        //   shadowBlur={2}
        //   shadowOpacity={0.1}
        //   shadowOffsetX={1}
        //   shadowOffsetY={1}
        //   shadowForStrokeEnabled
        // />
        null}

        {pump_hoger ? (
          <Group offsetX={-groupWidth + 14} offsetY={16}>
            <Image width={10} height={10} title="image" url={holeImage} />
          </Group>
        ) : // <Circle
        //   width={6}
        //   height={6}
        //   fill="#fff"
        //   x={groupWidth - 10}
        //   y={-10}
        //   shadowBlur={2}
        //   shadowOpacity={0.1}
        //   shadowOffsetX={1}
        //   shadowOffsetY={1}
        //   shadowForStrokeEnabled
        // />
        null}

        {blandare ? (
          // <Circle
          //   width={12}
          //   height={12}
          //   fill="#fff"
          //   x={groupWidth / 2}
          //   y={-10}
          //   shadowBlur={2}
          //   shadowOpacity={0.1}
          //   shadowOffsetX={1}
          //   shadowOffsetY={1}
          //   shadowForStrokeEnabled
          // />
          <Group offsetX={-(groupWidth - 14) / 2} offsetY={18}>
            <Image width={14} height={14} title="image" url={holeImage} />
          </Group>
        ) : null}
      </Group>
    </Group>
  );
};
export default SinkGroups;
